// AlumniHero.jsx
import React from "react";
import PropTypes from "prop-types";
import { Button, Space, Typography } from "antd";

const { Title, Paragraph } = Typography;

/**
 * AlumniHero (photo background)
 *
 * Props:
 * - collegeName: optional string shown in small badge
 * - onJoin: callback for primary CTA
 * - illustrationSrc: optional image path (defaults to /images/world-globe.png)
 *
 * Note: put your background image in public/images/world-globe.png
 */

export default function AlumniHero({
  collegeName = "PSG Institute of Advanced Studies",
  onJoin,
  illustrationSrc = "https://t3.ftcdn.net/jpg/06/59/79/48/360_F_659794827_LShQbouGqVWkfxJEHaoRxxqA5Cd53dkH.jpg",
}) {
  return (
    <>
      <style>{`
        .alumni-hero {
          position: relative;
          overflow: hidden;
          width: 100%;
          background: linear-gradient(180deg, #eef2ff 0%, #f7f9ff 70%, #fbfbff 100%);
          color: #222;
          padding: 100px 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        @media (max-width: 768px) {
          .alumni-hero { padding: 64px 16px; }
          .alumni-hero h1 { font-size: 1.6rem; line-height: 1.15; }
          .alumni-hero .hero-sub { font-size: 0.95rem; }
        }

        .alumni-hero-inner {
          max-width: 920px;
          width: 100%;
          z-index: 3;
          position: relative;
          display:flex;
          flex-direction:column;
          align-items:center;
          gap: 20px;
          padding: 0 12px;
        }

        /* background photo */
        .hero-bg-photo {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          pointer-events: none;
          opacity: 0.12;
          filter: blur(2px) saturate(110%);
          transform: scale(1.02);
          z-index: 1;
        }

        /* small badge */
        .hero-badge {
          display:inline-block;
          background: rgba(11,92,255,0.08);
          color: #0b5cff;
          padding: 6px 12px;
          border-radius: 999px;
          font-weight: 600;
          font-size: 0.9rem;
          letter-spacing: 0.2px;
          margin-bottom: 8px;
          opacity: 0;
          transform: translateY(8px);
          animation: fadeUp 520ms ease forwards;
          animation-delay: 120ms;
        }

        .hero-title {
          margin: 0;
          font-size: 2.6rem;
          line-height: 1.05;
          font-weight: 700;
          color: #111827;
          opacity: 0;
          transform: translateY(14px);
          animation: fadeUp 520ms ease forwards;
          animation-delay: 220ms;
        }

        .hero-sub {
          max-width: 720px;
          margin: 0;
          color: #374151;
          font-size: 1.05rem;
          line-height: 1.6;
          opacity: 0;
          transform: translateY(14px);
          animation: fadeUp 520ms ease forwards;
          animation-delay: 320ms;
        }

        .hero-ctas {
          margin-top: 14px;
          display:flex;
          gap: 12px;
          align-items:center;
          justify-content:center;
          opacity: 0;
          transform: translateY(18px);
          animation: fadeUp 520ms ease forwards;
          animation-delay: 420ms;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <section className="alumni-hero" role="region" aria-label="Hero section">
        {/* Background photo (faded) */}
        {illustrationSrc && (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img src={illustrationSrc} className="hero-bg-photo" aria-hidden />
        )}

        <div className="alumni-hero-inner">
          {collegeName && (
            <div className="hero-badge" aria-hidden>
              Welcome to {collegeName} Alumni Network
            </div>
          )}

          <Title level={2} className="hero-title">
            Empowering Global Connections of PSG IAN
          </Title>

          <Paragraph className="hero-sub" aria-live="polite">
            Reconnect with fellow scholars, celebrate achievements, and collaborate across industries through the PSG Institute of Advanced Studies Alumni Network.
          </Paragraph>

          <div className="hero-ctas" role="group" aria-label="Call to action">
            <Space size="middle">
              <Button
                type="primary"
                size="large"
                onClick={() => (onJoin ? onJoin() : (window.location.href = "register"))}
                aria-label="Join the Community"
              >
                Register Now
              </Button>
            </Space>
          </div>
        </div>
      </section>
    </>
  );
}

AlumniHero.propTypes = {
  collegeName: PropTypes.string,
  onJoin: PropTypes.func,
  illustrationSrc: PropTypes.string,
};
